import * as indicatorTypes from "@chartLib/lib/indicator";
import * as seriesTypes from "@chartLib/lib/series";
import * as tooltipTypes from "@chartLib/lib/tooltip";
import * as coordinateTypes from "@chartLib/lib/coordinates";
import {
  WindowSizeType,
  NumericPropertyType,
  SourcePathType,
  StrokeType,
  HistogramBarType,
  ColorType,
  BooleanType,
  FillType,
  SelectType,
} from "../PropertyTypes";

const trendRegistry = {
  displayName: "Tendência",
  indicators: {
    // VHF — Filtro Vertical Horizontal (migrado do Silverlight FML.VHF).
    // Categoria legada "Outros"; fica em Tendência por ser um medidor de força
    // de tendência (mesma família do ADX).
    vhf: {
      displayName: "Filtro Vertical Horizontal (VHF)",
      key: "vhf",
      type: indicatorTypes.vhf,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "VHF",
      renderKey: "vhf",
      decimalPlaces: 4,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 28,
          min: 1,
          max: 1000,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          // Cor default do engine (1ª série da paleta por índice).
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "VHF",
      },
      // Razão ≥ 0 (na prática ≤ 1) — auto-fit com piso em zero.
      yExtentKeys: ["vhf"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.vhf)
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [0, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 0.01);
        return [Math.max(0, min - padding), max + padding];
      },
      description:
        "Filtro Vertical Horizontal: compara a amplitude do movimento no período com a soma das variações barra a barra, indicando se o mercado está em tendência (valores altos) ou lateral (valores baixos).",
    },
    macd: {
      displayName: "M.A. Convergence Divergence (MACD)",
      key: "macd",
      type: indicatorTypes.macd,
      series: seriesTypes.MACDSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      labelConfig: {
        lines: [
          { key: "macd", colorPath: "strokeMACD.color" },
          { key: "signal", colorPath: "strokeSignal.color" },
        ],
      },
      defaultOptions: [
        // Issue #340: max elevado de 50 para 9999 (paridade com a PTZ, que
        // aceita até 610). O calculador usa EMA e lida com janelas grandes.
        NumericPropertyType({
          name: "fast",
          displayName: "Rápido",
          min: 1,
          max: 9999,
          defaultValue: 12,
        }),
        NumericPropertyType({
          name: "slow",
          displayName: "Lento",
          min: 1,
          max: 9999,
          defaultValue: 26,
        }),
        NumericPropertyType({
          name: "signal",
          displayName: "Sinal",
          min: 1,
          max: 9999,
          defaultValue: 9,
        }),
        SourcePathType({ defaultValue: "close" }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeMACD",
          displayName: "Cor MACD",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeSignal",
          displayName: "Cor Sinal",
          defaultValue: {
            color: "#00F300",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Cor Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        HistogramBarType({
          name: "barDivergence",
          displayName: "Histograma",
          defaultValue: {
            increasingColor: "#8cc176",
            decreasingColor: "#b82c0c",
            opacity: 0.6,
            width: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.MACDTooltip,
    },
    adx: {
      displayName: "ADX/DMS (Average Directional Index)",
      key: "adx",
      type: indicatorTypes.adx,
      series: seriesTypes.ADXSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      defaultOptions: [
        WindowSizeType({ defaultValue: 14 }),
        NumericPropertyType({
          name: "smoothingPeriod",
          displayName: "Período de Suavização",
          min: 1,
          max: 100,
          defaultValue: 14,
        }),
      ],
      defaultOptionsForAppearance: [
        BooleanType({
          name: "showSeries",
          displayName: "Mostrar Linhas",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        BooleanType({
          name: "showShading",
          displayName: "Preenchimento +DI/-DI",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        BooleanType({
          name: "showHistogram",
          displayName: "Histograma",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        StrokeType({
          name: "positiveDIStroke",
          displayName: "Cor +DI",
          defaultValue: {
            color: "#00FF00",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "negativeDIStroke",
          displayName: "Cor -DI",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "adxStroke",
          displayName: "Cor ADX",
          defaultValue: {
            color: "#FFD700",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        ColorType({
          name: "positiveHistogramColor",
          displayName: "Cor Barra Positiva",
          defaultValue: { color: "#00DD00" },
        }),
        ColorType({
          name: "negativeHistogramColor",
          displayName: "Cor Barra Negativa",
          defaultValue: { color: "#FF0000" },
        }),
      ],
      tooltip: tooltipTypes.ADXTooltip,
      yExtents: (plotData, indicator) => {
        const { ADXSeries } = seriesTypes;
        return ADXSeries.yExtents(plotData, undefined, plotData, indicator);
      },
      yAxisId: "ADX",
      separatePanel: true,
      renderKey: "adx",
      yAxis: {
        label: "ADX",
      },
      description:
        "Average Directional Index com linhas +DI/-DI e opções de preenchimento/histograma para medir força de tendência.",
    },
    hiloActivator: {
      displayName: "HiLo Activator",
      key: "hiloActivator",
      type: indicatorTypes.hiloActivator,
      series: seriesTypes.HiLoActivatorSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        NumericPropertyType({
          name: "highPeriod",
          displayName: "Período Máxima",
          min: 1,
          max: 100,
          defaultValue: 3,
        }),
        NumericPropertyType({
          name: "lowPeriod",
          displayName: "Período Mínima",
          min: 1,
          max: 100,
          defaultValue: 3,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "longStroke",
          displayName: "Cor Linha Long",
          defaultValue: {
            color: "#2ecc71",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "shortStroke",
          displayName: "Cor Linha Short",
          defaultValue: {
            color: "#ff4d4d",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.HiLoActivatorTooltip,
      renderKey: "hiloActivator",
      yExtentKeys: ["P1", "P2"],
      description:
        "Indicador HiLo Activator exibindo stops móveis acima/abaixo do preço conforme a tendência.",
    },
    supertrend: {
      displayName: "Supertrend",
      key: "supertrend",
      type: indicatorTypes.supertrend,
      series: seriesTypes.SupertrendSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período",
          min: 1,
          max: 100,
          defaultValue: 7,
        }),
        NumericPropertyType({
          name: "multiplier",
          displayName: "Multiplicador",
          min: 0.1,
          step: 0.1,
          defaultValue: 3,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "upStroke",
          displayName: "Cor Tendência Alta",
          defaultValue: {
            color: "#8cc176",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "downStroke",
          displayName: "Cor Tendência Baixa",
          defaultValue: {
            color: "#b82c0c",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "uptrend", colorPath: "upStroke.color" },
          { key: "downtrend", colorPath: "downStroke.color" },
        ],
      },
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "Supertrend",
      },
      renderKey: "supertrend",
      yExtentKeys: ["value"],
      includeInMainExtent: false,
      description:
        "Indicador Supertrend exibindo a linha de tendência baseada em ATR acima/abaixo do preço.",
    },
    atrStop: {
      displayName: "ATR Trailing Stop",
      key: "atrStop",
      type: indicatorTypes.atrStop,
      series: seriesTypes.ATRStopSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        WindowSizeType({ name: "period", defaultValue: 21 }),
        NumericPropertyType({
          name: "multiplier",
          displayName: "Multiplicador",
          min: 0.1,
          max: 10,
          defaultValue: 3,
        }),
        SourcePathType({
          name: "plotType",
          displayName: "Tipo de Parcela",
          options: [
            { value: "points", label: "Pontos" },
            { value: "squarewave", label: "Escada" },
          ],
          defaultValue: "points",
        }),
        BooleanType({
          name: "useHighLow",
          displayName: "Usar Máxima/Mínima",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "fillRising",
          displayName: "Cor ATR+",
          defaultValue: {
            color: "#00FF00",
            dash: "Solid",
            width: 1,
            opacity: 0.8,
          },
        }),
        StrokeType({
          name: "fillFalling",
          displayName: "Cor ATR-",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 0.8,
          },
        }),
      ],
      tooltip: tooltipTypes.ATRStopTooltip,
      renderKey: "atrStop",
      description:
        "Stops móveis baseados em ATR que alternam entre suporte e resistência conforme a direção do preço.",
    },
    sar: {
      displayName: "Parabolic SAR",
      key: "sar",
      type: indicatorTypes.sar,
      series: seriesTypes.SARSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        // Os três parâmetros do engine (FML.SAR): AF inicial, incremento e teto.
        // O `accelerationFactor` também é o valor de RESET a cada virada.
        NumericPropertyType({
          name: "accelerationFactor",
          displayName: "Fator de Aceleração",
          min: 0.01,
          max: 0.1,
          defaultValue: 0.02,
        }),
        // Incremento aplicado ao AF quando o EP melhora. Ficava colado no AF
        // inicial (um número fazia os dois papéis) até 2026-07-22.
        NumericPropertyType({
          name: "accelerationStep",
          displayName: "Passo de Aceleração",
          min: 0.01,
          max: 0.1,
          defaultValue: 0.02,
        }),
        NumericPropertyType({
          name: "maxAccelerationFactor",
          displayName: "Aceleração Máxima",
          min: 0.1,
          max: 0.5,
          defaultValue: 0.2,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "fillRising",
          displayName: "Cor SAR Alta",
          defaultValue: {
            color: "#00FF00",
            dash: "Solid",
            width: 1,
            opacity: 0.8,
          },
        }),
        StrokeType({
          name: "fillFalling",
          displayName: "Cor SAR Baixa",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 0.8,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "SAR",
      },
      renderKey: "sar", // This should match the accessor in the indicator
    },
    supportResistance: {
      displayName: "Support & Resistance",
      key: "supportResistance",
      type: indicatorTypes.supportResistance,
      series: seriesTypes.SupportResistanceSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        BooleanType({
          name: "channelFill",
          displayName: "Preenchimento Canal",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "supportStroke",
          displayName: "Linha de Suporte",
          defaultValue: {
            color: "#80c080",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "resistanceStroke",
          displayName: "Linha de Resistência",
          defaultValue: {
            color: "#80c080",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        FillType({
          name: "channelFillColor",
          displayName: "Cor do Canal",
          defaultValue: { color: "#80c080", opacity: 0.1 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "support", colorPath: "supportStroke.color" },
          { key: "resistance", colorPath: "resistanceStroke.color" },
        ],
      },
      tooltip: tooltipTypes.SupportResistanceTooltip,
      renderKey: "supportResistance",
      yExtentKeys: ["support", "resistance"],
      description:
        "Linhas dinâmicas de suporte e resistência baseadas na relação entre máxima, mínima e fechamento do período atual.",
    },
    zigzag: {
      displayName: "ZigZag",
      key: "zigzag",
      type: indicatorTypes.zigzag,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        NumericPropertyType({
          name: "distance",
          displayName: "Distância (%)",
          min: 0.1,
          max: 100,
          step: 0.1,
          defaultValue: 10,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#FF6B35",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "ZigZag",
      },
      renderKey: "zigzag",
      description:
        "Conecta pontos de reversão (pivot highs/lows) quando a mudança de preço excede a distância percentual especificada. Útil para identificar tendências e padrões de preço.",
    },
    ichimoku: {
      displayName: "Ichimoku Clouds",
      key: "ichimoku",
      type: indicatorTypes.ichimoku,
      series: seriesTypes.IchimokuSeries,
      futureOverlayComponent: seriesTypes.IchimokuFutureCloudOverlay, // issue #161 — mounted by ChartRenderer, not IndicatorRenderer
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      includeInMainExtent: true,
      defaultOptions: [
        NumericPropertyType({
          name: "conversionPeriod",
          displayName: "Período de Conversão",
          min: 1,
          max: 100,
          defaultValue: 9,
        }),
        NumericPropertyType({
          name: "basePeriod",
          displayName: "Período Base",
          min: 1,
          max: 100,
          defaultValue: 26,
        }),
        NumericPropertyType({
          name: "spanBPeriod",
          displayName: "Intervalo de Liderança (B)",
          min: 1,
          max: 200,
          defaultValue: 52,
        }),
        NumericPropertyType({
          name: "displacement",
          displayName: "Período de Duração de Lag",
          min: 1,
          max: 100,
          defaultValue: 26,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "conversionLine",
          displayName: "Linha de Conversão",
          defaultValue: {
            color: "#2196F3",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "baseLine",
          displayName: "Linha de Base",
          defaultValue: {
            color: "#FF9800",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "spanA",
          displayName: "Intervalo de Liderança A",
          defaultValue: {
            color: "#4CAF50",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "spanB",
          displayName: "Intervalo de Liderança B",
          defaultValue: {
            color: "#F44336",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "laggingSpan",
          displayName: "Lagging Span",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "conversionLine", colorPath: "conversionLine.color" },
          { key: "baseLine", colorPath: "baseLine.color" },
          { key: "spanA", colorPath: "spanA.color" },
          { key: "spanB", colorPath: "spanB.color" },
          { key: "laggingSpan", colorPath: "laggingSpan.color" },
        ],
      },
      tooltip: tooltipTypes.IchimokuTooltip,
      renderKey: "ichimoku",
      yExtentKeys: ["conversionLine", "baseLine", "spanA", "spanB"],
      description:
        "Sistema de negociação japonês com 5 linhas: Conversion, Base, Leading Span A/B (formam a nuvem Kumo) e Lagging Span. Identifica tendências, suporte/resistência e momentum.",
    },
    alligator: {
      displayName: "Alligator",
      key: "alligator",
      type: indicatorTypes.alligator,
      series: seriesTypes.AlligatorSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      labelConfig: {
        lines: [
          { key: "jaw", colorPath: "strokeJaw.color" },
          { key: "teeth", colorPath: "strokeTeeth.color" },
          { key: "lips", colorPath: "strokeLips.color" },
        ],
      },
      defaultOptions: [
        NumericPropertyType({
          name: "jawPeriod",
          displayName: "Período Mandíbula",
          min: 1,
          max: 100,
          defaultValue: 13,
        }),
        NumericPropertyType({
          name: "jawOffset",
          displayName: "Offset Mandíbula",
          min: 0,
          max: 50,
          defaultValue: 8,
        }),
        NumericPropertyType({
          name: "teethPeriod",
          displayName: "Período dentes",
          min: 1,
          max: 100,
          defaultValue: 8,
        }),
        NumericPropertyType({
          name: "teethOffset",
          displayName: "Offset dentes",
          min: 0,
          max: 50,
          defaultValue: 5,
        }),
        NumericPropertyType({
          name: "lipsPeriod",
          displayName: "Período lábios",
          min: 1,
          max: 100,
          defaultValue: 5,
        }),
        NumericPropertyType({
          name: "lipsOffset",
          displayName: "Offset lábios",
          min: 0,
          max: 50,
          defaultValue: 3,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeJaw",
          displayName: "Linha Queixo (Jaw)",
          defaultValue: {
            color: "#0000FF",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeTeeth",
          displayName: "Linha Dentes (Teeth)",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeLips",
          displayName: "Linha Lábios (Lips)",
          defaultValue: {
            color: "#00DD00",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.AlligatorTooltip,
      renderKey: "alligator",
      yExtentKeys: ["jaw", "teeth", "lips"],
      description:
        "Indicador Alligator de Bill Williams usa 3 médias móveis suavizadas (SMMA) com deslocamento para identificar tendências: Jaw (Queixo - azul, 13), Teeth (Dentes - vermelho, 8) e Lips (Lábios - verde, 5).",
    },
    bbi: {
      displayName: "Bullish-Bearish (BBI)",
      key: "bbi",
      type: indicatorTypes.bbi,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      renderKey: "bbi",
      defaultOptions: [
        // min/max fiéis ao AddParam do engine (0–1000; N=0 = média cumulativa,
        // suportado pelo silverlightDsl.ma).
        NumericPropertyType({
          name: "n1",
          displayName: "N1",
          min: 0,
          max: 1000,
          defaultValue: 3,
        }),
        NumericPropertyType({
          name: "n2",
          displayName: "N2",
          min: 0,
          max: 1000,
          defaultValue: 6,
        }),
        NumericPropertyType({
          name: "n3",
          displayName: "N3",
          min: 0,
          max: 1000,
          defaultValue: 12,
        }),
        NumericPropertyType({
          name: "n4",
          displayName: "N4",
          min: 0,
          max: 1000,
          defaultValue: 24,
        }),
        SourcePathType({ defaultValue: "close", allowSeriesSource: true }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          // Cor default = cor do engine Silverlight (série sem SetAttrs → amarelo
          // do engine, mesmo padrão de VRSI/RSICutler/TEMA). Ver LICOES.md.
          defaultValue: {
            color: "#FFFF00",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "BBI",
        displayFormat: (d) => d?.toFixed(2),
      },
      description:
        "Indicador Bullish-Bearish (BBI): média das médias móveis aritméticas de 4 períodos (3, 6, 12 e 24) do fechamento, plotada sobre o preço.",
    },
    didiHist: {
      displayName: "DIDI - Histograma (DIDIHIST)",
      key: "didiHist",
      type: indicatorTypes.didiHist,
      series: seriesTypes.ColoredHistogramSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "DIDIHIST",
      renderKey: "didiHist",
      decimalPlaces: 4,
      defaultOptions: [
        NumericPropertyType({
          name: "highPeriod",
          displayName: "High (curta)",
          min: 1,
          max: 1000,
          defaultValue: 3,
        }),
        NumericPropertyType({
          name: "midPeriod",
          displayName: "Center (média)",
          min: 1,
          max: 1000,
          defaultValue: 8,
        }),
        NumericPropertyType({
          name: "lowPeriod",
          displayName: "Low (longa)",
          min: 1,
          max: 1000,
          defaultValue: 20,
        }),
        SourcePathType({ defaultValue: "close" }),
      ],
      defaultOptionsForAppearance: [
        // Histograma COLORSTICK do engine: colore por sinal (>0 / ≤0).
        ColorType({
          name: "increasingColor",
          displayName: "Cor Positiva",
          defaultValue: { color: "#00DD00" },
        }),
        ColorType({
          name: "decreasingColor",
          displayName: "Cor Negativa",
          defaultValue: { color: "#FF0000" },
        }),
        // Center do engine = ColorRed + StyleDot (explícito na fórmula).
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#FF0000",
            dash: "Dot",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      transformAppearanceConfig: (config) => ({
        ...config,
        increasingColor: config.increasingColor?.color || "#00DD00",
        decreasingColor: config.decreasingColor?.color || "#FF0000",
        strokeZeroLine: config.strokeZeroLine || {
          color: "#FF0000",
          dash: "Dot",
          width: 1,
          opacity: 1,
        },
      }),
      tooltip: tooltipTypes.DidiHistTooltip,
      description:
        "DIDI - Histograma: painel próprio com histograma = (MA(High)−MA(Center)) − (MA(Low)−MA(Center)), colorido por sinal, e linha zero pontilhada. Períodos padrão 3/8/20.",
    },
    ddi: {
      displayName: "Índice Divergência Direcional 2 (DDI)",
      key: "ddi",
      type: indicatorTypes.ddi,
      series: seriesTypes.DDISeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "DDI",
      renderKey: "ddi",
      decimalPlaces: 4,
      defaultOptions: [
        NumericPropertyType({
          name: "n",
          displayName: "N (SUM)",
          min: 1,
          max: 1000,
          defaultValue: 13,
        }),
        NumericPropertyType({
          name: "n1",
          displayName: "N1 (ADDI)",
          min: 1,
          max: 1000,
          defaultValue: 30,
        }),
        NumericPropertyType({
          name: "m",
          displayName: "M (peso ADDI)",
          min: 1,
          max: 1000,
          defaultValue: 10,
        }),
        NumericPropertyType({
          name: "m1",
          displayName: "M1 (AD)",
          min: 1,
          max: 1000,
          defaultValue: 5,
        }),
      ],
      defaultOptionsForAppearance: [
        // Histograma DDI = COLORSTICK (colore por sinal); linhas ADDI/AD.
        // Cores default do engine colhidas no OOB via UIA.
        ColorType({
          name: "increasingColor",
          displayName: "Cor Positiva (DDI)",
          defaultValue: { color: "#00DD00" },
        }),
        ColorType({
          name: "decreasingColor",
          displayName: "Cor Negativa (DDI)",
          defaultValue: { color: "#FF0000" },
        }),
        // Cores default do engine (colhidas no OOB): ADDI=Red, AD=#0000B4.
        StrokeType({
          name: "strokeAddi",
          displayName: "Cor ADDI",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeAd",
          displayName: "Cor AD",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      transformAppearanceConfig: (config) => ({
        ...config,
        increasingColor: config.increasingColor?.color || "#00DD00",
        decreasingColor: config.decreasingColor?.color || "#FF0000",
      }),
      tooltip: tooltipTypes.DDITooltip,
      description:
        "Índice de Divergência Direcional 2: painel próprio com histograma (DIZ−DIF, colorido por sinal) e duas linhas de suavização (ADDI, AD). Não confundir com DIDI (Linhas).",
    },
    // VMACD — MACD do Volume (migrado do Silverlight FML.VMACD, categoria
    // Tendência). O MACD clássico aplicado ao VOLUME: DIFF=EMA(VOL,SHORT)−
    // EMA(VOL,LONG), DEA=EMA(DIFF,M), MACD=DIFF−DEA (COLORSTICK).
    // Difusor de Fluxo — migrado do Silverlight (FML.DF, "Arena do Investidor"/
    // "Trade ao Vivo"; FMLXPI.cs:143 ≡ FMLClear.cs:163 ≡ FMLRico.cs:172).
    // Derivado do MACD: 3 linhas em painel próprio, SEM histograma. Cores
    // default = SetAttrs do engine (DIFF #FF392B, DF− #FFAE16, DF+ #FFFFFF).
    difusorFluxo: {
      displayName: "Difusor de Fluxo (DF)",
      key: "difusorFluxo",
      type: indicatorTypes.difusorFluxo,
      series: seriesTypes.DifusorFluxoSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "DifusorFluxo",
      renderKey: "difusorFluxo",
      decimalPlaces: 2,
      defaultOptions: [
        NumericPropertyType({
          name: "long",
          displayName: "Média Longa (LONG)",
          min: 1,
          max: 1000,
          defaultValue: 55,
        }),
        NumericPropertyType({
          name: "short",
          displayName: "Média Curta (SHORT)",
          min: 1,
          max: 1000,
          defaultValue: 21,
        }),
        NumericPropertyType({
          name: "dfMenos",
          displayName: "Média DF− (DF_MENOS)",
          min: 1,
          max: 1000,
          defaultValue: 34,
        }),
        NumericPropertyType({
          name: "dfMais",
          displayName: "Média DF+ (DF_MAIS)",
          min: 1,
          max: 1000,
          defaultValue: 89,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeDiff",
          displayName: "Cor Difusor",
          defaultValue: {
            color: "#FF392B",
            width: 2,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeDfMenos",
          displayName: "Cor DF−",
          defaultValue: {
            color: "#FFAE16",
            width: 2,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeDfMais",
          displayName: "Cor DF+",
          defaultValue: {
            color: "#FFFFFF",
            width: 2,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "diff", colorPath: "strokeDiff.color" },
          { key: "dfMenos", colorPath: "strokeDfMenos.color" },
          { key: "dfMais", colorPath: "strokeDfMais.color" },
        ],
      },
      tooltip: tooltipTypes.DifusorFluxoTooltip,
      description:
        "Difusor de Fluxo: derivado do MACD com uma média extra de longo período. O Difusor é a diferença entre as médias exponenciais curta e longa do preço; DF− e DF+ são médias exponenciais do próprio Difusor (médio e longo prazo). A configuração padrão usa 21/55 para o Difusor, 34 para o DF− e 89 para o DF+.",
    },
    vmacd: {
      displayName: "MACD do Volume (VMACD)",
      key: "vmacd",
      type: indicatorTypes.vmacd,
      series: seriesTypes.VMACDSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "VMACD",
      renderKey: "vmacd",
      decimalPlaces: 2,
      defaultOptions: [
        NumericPropertyType({
          name: "short",
          displayName: "Média Curta (SHORT)",
          min: 1,
          max: 1000,
          defaultValue: 12,
        }),
        NumericPropertyType({
          name: "long",
          displayName: "Média Longa (LONG)",
          min: 1,
          max: 1000,
          defaultValue: 26,
        }),
        NumericPropertyType({
          name: "m",
          displayName: "Sinal (M)",
          min: 1,
          max: 1000,
          defaultValue: 9,
        }),
      ],
      defaultOptionsForAppearance: [
        // Histograma MACD = COLORSTICK (colore por sinal, ignora a cor nominal
        // do índice 2). Linhas DIFF/DEA seguem a paleta default do engine por
        // ÍNDICE de série no FormulaPackage: DIFF=Yellow(0), DEA=Red(1).
        ColorType({
          name: "increasingColor",
          displayName: "Cor Positiva (MACD)",
          defaultValue: { color: "#00DD00" },
        }),
        ColorType({
          name: "decreasingColor",
          displayName: "Cor Negativa (MACD)",
          defaultValue: { color: "#FF0000" },
        }),
        StrokeType({
          name: "strokeDiff",
          displayName: "Cor DIFF",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeDea",
          displayName: "Cor DEA",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      transformAppearanceConfig: (config) => ({
        ...config,
        increasingColor: config.increasingColor?.color || "#00DD00",
        decreasingColor: config.decreasingColor?.color || "#FF0000",
      }),
      tooltip: tooltipTypes.VMACDTooltip,
      description:
        "MACD aplicado ao volume negociado: a diferença entre duas médias exponenciais do volume (DIFF), sua média de sinal (DEA) e o histograma da diferença entre as duas, colorido por sinal.",
    },
    linRegrChannel: {
      displayName: "Canais de Regressão Linear (LinRegr)",
      key: "linRegrChannel",
      type: indicatorTypes.linRegrChannel,
      series: seriesTypes.BollingerSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      renderKey: "linRegrChannel",
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período (N)",
          min: 2,
          max: 1000,
          defaultValue: 14,
        }),
        NumericPropertyType({
          name: "percent",
          displayName: "Percentual (P)",
          min: 0,
          max: 100,
          defaultValue: 100,
        }),
        SourcePathType({ defaultValue: "close" }),
      ],
      defaultOptionsForAppearance: [
        // Cores default = engine, colhidas no OOB via UIA (paleta por índice de
        // série [Yellow, Red, #0000B4]): Upper=Yellow, Lower=Red, A(reta)=#0000B4.
        StrokeType({
          name: "strokeTop",
          displayName: "Cor Superior (Upper)",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMiddle",
          displayName: "Cor Regressão (A)",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBottom",
          displayName: "Cor Inferior (Lower)",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        // Engine plota 3 LINHAS, sem FILLRGN → sem preenchimento (opacity 0).
        FillType({
          name: "fill",
          displayName: "Cor Preenchimento",
          defaultValue: { color: "#4682B4", opacity: 0 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "top", colorPath: "strokeTop.color" },
          { key: "middle", colorPath: "strokeMiddle.color" },
          { key: "bottom", colorPath: "strokeBottom.color" },
        ],
      },
      tooltip: tooltipTypes.LinRegrChannelTooltip,
      description:
        "Canais de Regressão Linear: canal estático sobre as últimas N barras — reta de regressão (A) e bandas paralelas A ± Percentual·(desvio máximo do preço à reta). Overlay de preço.",
    },
    linearForecast: {
      displayName: "Previsão Linear (Linear Forecast)",
      key: "linearForecast",
      type: indicatorTypes.linearForecast,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      renderKey: "linearForecast",
      defaultOptions: [
        WindowSizeType({ defaultValue: 14 }),
        SourcePathType({ defaultValue: "close", allowSeriesSource: true }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#FF6B35",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "Lin Fcst",
        displayFormat: (d) => d?.toFixed(2),
      },
      description:
        "Calcula uma previsão de regressão linear projetando a linha de tendência para frente pelo comprimento do período. Usa o método dos mínimos quadrados para ajustar uma linha através dos últimos N períodos.",
    },
    linearRegressionIntercept: {
      displayName: "Interceção Reg. Linear (Lin Reg Intercept)",
      key: "linearRegressionIntercept",
      type: indicatorTypes.linearRegressionIntercept,
      series: seriesTypes.LinearRegressionInterceptSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      renderKey: "linearRegressionIntercept",
      defaultOptions: [
        WindowSizeType({ defaultValue: 14 }),
        SourcePathType({ defaultValue: "close", allowSeriesSource: true }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Cor da Linha",
          defaultValue: {
            color: "#E040FB",
            dash: "Solid",
            width: 2,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "Lin Incpt",
        displayFormat: (d) => d?.toFixed(2),
      },
      description:
        "Calcula o intercepto (ponto de partida) da linha de regressão linear dos últimos N períodos usando o método dos mínimos quadrados. Mostra onde a reta de tendência cruza o eixo Y no início da janela.",
    },
    pivotPoints: {
      displayName: "Pontos de Pivô (Pivot Points)",
      key: "pivotPoints",
      type: indicatorTypes.pivotPoints,
      series: seriesTypes.PivotPointsSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      includeInMainExtent: true,
      drawOnMainChart: true,
      renderKey: "pivotPoints",
      yExtentKeys: ["pivot", "r1", "r2", "r3", "s1", "s2", "s3"],
      labelConfig: {
        lines: [
          { key: "pivot", colorPath: "strokePivot.color" },
          { key: "r1", colorPath: "strokeR1.color" },
          { key: "r2", colorPath: "strokeR2.color" },
          { key: "r3", colorPath: "strokeR3.color" },
          { key: "s1", colorPath: "strokeS1.color" },
          { key: "s2", colorPath: "strokeS2.color" },
          { key: "s3", colorPath: "strokeS3.color" },
        ],
      },
      defaultOptions: [
        SelectType({
          name: "type",
          displayName: "Tipo",
          options: [
            { label: "padrão", value: "standard" },
            { label: "fibonacci", value: "fibonacci" },
          ],
          defaultValue: "standard",
        }),
        BooleanType({
          name: "continuous",
          displayName: "Contínuo",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
          hint: "Linhas fluidas conectadas (Sim) ou linhas horizontais segmentadas (Não)",
        }),
        BooleanType({
          name: "staticAreas",
          displayName: "Sombreamento",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
          hint: "Preenche as zonas entre os níveis de pivô com cores (apenas em modo normal)",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokePivot",
          displayName: "Eixo",
          defaultValue: {
            color: "#9C27B0",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeR1",
          displayName: "Resistência 1",
          defaultValue: {
            color: "#B82C0B",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeR2",
          displayName: "Resistência 2",
          defaultValue: {
            color: "#E36460",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeR3",
          displayName: "Resistência 3",
          defaultValue: {
            color: "#FFD0CF",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeS1",
          displayName: "Suporte 1",
          defaultValue: {
            color: "#699158",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeS2",
          displayName: "Suporte 2",
          defaultValue: {
            color: "#B3D987",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeS3",
          displayName: "Apoio 3",
          defaultValue: {
            color: "#D3E8AE",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.PivotPointsTooltip,
      tooltipProps: {
        labels: {
          pivot: "Eixo",
          r1: "Resistência 1",
          r2: "Resistência 2",
          r3: "Resistência 3",
          s1: "Suporte 1",
          s2: "Suporte 2",
          s3: "Apoio 3",
        },
      },
      description:
        "Pontos de Pivô calculam níveis de suporte e resistência baseados no High, Low e Close do período anterior. Oferece duas fórmulas: Padrão (suporte/resistência tradicional) e Fibonacci (usando níveis de 38.2%, 61.8% e 100%). O período é detectado automaticamente baseado no timeframe do gráfico: Diário (até 15min), Semanal (15min-1D), Mensal (diário+). Modo Contínuo exibe linhas fluidas que recalculam a cada barra, enquanto modo normal mostra linhas horizontais que mudam apenas nos limites do período. Sombreamento preenche as zonas entre os níveis com cores semi-transparentes (vermelho para resistências, verde para suportes).",
    },
    valuationLines: {
      displayName: "Linhas de Avaliação (Valuation Lines)",
      key: "valuationLines",
      type: indicatorTypes.valuationLines,
      series: seriesTypes.ValuationLinesSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      includeInMainExtent: false,
      drawOnMainChart: true,
      renderKey: "valuationLines",
      defaultOptions: [
        SourcePathType({ defaultValue: "close" }),
        SelectType({
          name: "averageType",
          displayName: "Tipo de Média",
          options: [
            { label: "Média Aritmética", value: "mean" },
            { label: "Mediana", value: "median" },
            { label: "Média Harmônica", value: "harmonic" },
          ],
          defaultValue: "mean",
        }),
      ],
      defaultOptionsForAppearance: [
        BooleanType({
          name: "displayAverage",
          displayName: "Exibir Média",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        BooleanType({
          name: "displayOneStdDev",
          displayName: "Exibir 1 Desvio Padrão (1σ)",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        BooleanType({
          name: "displayTwoStdDev",
          displayName: "Exibir 2 Desvios Padrão (2σ)",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        BooleanType({
          name: "displayThreeStdDev",
          displayName: "Exibir 3 Desvios Padrão (3σ)",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        StrokeType({
          name: "strokeAverage",
          displayName: "Cor Média",
          defaultValue: {
            color: "#00afed",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokePlusOne",
          displayName: "Cor +1σ",
          defaultValue: {
            color: "#e1e1e1",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeMinusOne",
          displayName: "Cor -1σ",
          defaultValue: {
            color: "#e1e1e1",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokePlusTwo",
          displayName: "Cor +2σ",
          defaultValue: {
            color: "#85c99e",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeMinusTwo",
          displayName: "Cor -2σ",
          defaultValue: {
            color: "#85c99e",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokePlusThree",
          displayName: "Cor +3σ",
          defaultValue: {
            color: "#fff69e",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeMinusThree",
          displayName: "Cor -3σ",
          defaultValue: {
            color: "#fff69e",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.ValuationLinesTooltip,
      tooltipProps: {
        yLabel: "Linhas de Avaliação",
        labels: {
          average: "Média",
          plusOneStdDev: "+1σ",
          minusOneStdDev: "-1σ",
          plusTwoStdDev: "+2σ",
          minusTwoStdDev: "-2σ",
          plusThreeStdDev: "+3σ",
          minusThreeStdDev: "-3σ",
        },
      },
      description:
        "Linhas de Avaliação exibem níveis estatísticos baseados nos dados visíveis no gráfico. Calcula a média (aritmética/mediana/harmônica) e até 3 níveis de desvio padrão (±1σ, ±2σ, ±3σ). As linhas são dinâmicas e recalculam automaticamente quando você faz zoom ou move o gráfico, sempre refletindo as estatísticas dos dados atualmente visíveis. Use para identificar se o preço atual está acima/abaixo da média do período visível e quantos desvios padrão está distante. Útil para análise estatística e identificação de extremos de preço.",
    },
    darvasBox: {
      displayName: "Darvas Box",
      key: "darvasBox",
      type: indicatorTypes.darvasBox,
      series: seriesTypes.DarvasBoxSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      includeInMainExtent: false,
      defaultOptions: [
        WindowSizeType({
          name: "athLookbackPeriod",
          displayName: "ATH Lookback Period",
          defaultValue: 100,
          min: 1,
          max: 500,
        }),
        SelectType({
          name: "exitField",
          displayName: "Exit Field",
          defaultValue: "high/low",
          options: [
            { label: "Close", value: "close" },
            { label: "High/Low", value: "high/low" },
          ],
        }),
        BooleanType({
          name: "ghostBoxes",
          displayName: "Ghost Boxes",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        BooleanType({
          name: "stopLevels",
          displayName: "Stop Levels",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        NumericPropertyType({
          name: "levelOffset",
          displayName: "Level Offset",
          defaultValue: 0.01,
          min: 0.01,
          step: 0.01,
        }),
        NumericPropertyType({
          name: "priceMinimum",
          displayName: "Price Minimum",
          defaultValue: 5,
          min: 0.01,
          step: 0.01,
        }),
        BooleanType({
          name: "volumeSpike",
          displayName: "Volume Spike Filter",
          defaultValue: false,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
        NumericPropertyType({
          name: "volumePercentOfAvg",
          displayName: "Volume % of Avg",
          defaultValue: 400,
          min: 1,
          max: 1000,
        }),
      ],
      defaultOptionsForAppearance: [
        FillType({
          name: "darvasFill",
          displayName: "Darvas Box",
          defaultValue: { color: "#5F7CB8", opacity: 0.25 },
        }),
        FillType({
          name: "ghostFill",
          displayName: "Ghost Box",
          defaultValue: { color: "#699158", opacity: 0.25 },
        }),
        StrokeType({
          name: "levelStroke",
          displayName: "Stop Level Line",
          defaultValue: {
            color: "#999999",
            dash: "ShortDash",
            width: 2,
            opacity: 0.8,
          },
        }),
      ],
      transformAppearanceConfig: (config) => {
        // Extract color and opacity from FillType objects for DarvasBoxSeries
        return {
          ...config,
          darvasColor: config.darvasFill?.color || "#5F7CB8",
          darvasFillOpacity: config.darvasFill?.opacity ?? 0.25,
          ghostColor: config.ghostFill?.color || "#699158",
          ghostFillOpacity: config.ghostFill?.opacity ?? 0.25,
        };
      },
      tooltip: tooltipTypes.DarvasBoxTooltip,
      renderKey: "darvasBox",
      yAxisId: "price",
      separatePanel: false,
      description:
        "Darvas Box é um sistema de trading desenvolvido por Nicolas Darvas que identifica zonas de consolidação (boxes) através da confirmação de topos e fundos. O indicador mostra boxes azuis (confirmadas), boxes verdes (projeções), e opcionalmente níveis de stop loss.",
    },
  },
};

export default trendRegistry;
